import { useEffect, useState } from 'react';
import { CalendarIcon, X } from 'lucide-react';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Return true for dates that must not be selectable. */
  disabledDate?: (date: Date) => boolean;
}

/**
 * Accepts what people actually type: 2026-07-14, 14.07.2026, 14/07/2026,
 * 14-07-2026 and bare 14072026. Returns null for anything that does not
 * round-trip to a real calendar date.
 */
function parseTypedDate(text: string): Date | null {
  const s = text.trim();
  let y: number;
  let m: number;
  let d: number;
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const dotted = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  const bare = s.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (iso) {
    y = Number(iso[1]); m = Number(iso[2]); d = Number(iso[3]);
  } else if (dotted) {
    d = Number(dotted[1]); m = Number(dotted[2]); y = Number(dotted[3]);
  } else if (bare) {
    d = Number(bare[1]); m = Number(bare[2]); y = Number(bare[3]);
  } else {
    return null;
  }
  const date = new Date(y, m - 1, d);
  const valid = date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
  return valid ? date : null;
}

const format = (date: Date) => dayjs(date).format('YYYY-MM-DD');

/**
 * Typable date field with a calendar fallback: the input parses manual
 * entry on blur/Enter, and the calendar popover has month + year dropdowns
 * so dates decades away are two clicks, not 120 arrow presses.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'RRRR-MM-DD',
  className,
  disabled,
  disabledDate,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value ? format(value) : '');

  const valueKey = value ? value.getTime() : null;
  useEffect(() => {
    setText(value ? format(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const commit = () => {
    if (text.trim() === '') {
      if (value) onChange(null);
      return;
    }
    const parsed = parseTypedDate(text);
    if (parsed && !disabledDate?.(parsed)) {
      onChange(parsed);
      setText(format(parsed));
    } else {
      // Invalid or disallowed input — snap back to the last committed value.
      setText(value ? format(value) : '');
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className={cn('relative w-full', className)}>
      <Input
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        className={cn('pr-14', !value && 'text-muted-foreground')}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
      {value && !disabled && (
        <button
          type="button"
          aria-label="Clear date"
          className="text-muted-foreground hover:bg-accent hover:text-foreground absolute top-1/2 right-8 -translate-y-1/2 rounded p-0.5"
          onClick={() => onChange(null)}
        >
          <X className="size-3" />
        </button>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Open calendar"
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-0.5 size-7 -translate-y-1/2"
          >
            <CalendarIcon className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            startMonth={new Date(1950, 0)}
            endMonth={new Date(currentYear + 10, 11)}
            disabled={disabledDate}
            selected={value ?? undefined}
            onSelect={(date) => {
              onChange(date ?? null);
              setOpen(false);
            }}
            defaultMonth={value ?? undefined}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
